// src/components/gestionEvento/CategoryDetailModal.jsx
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Stack, Chip, Typography, CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

const calcularRangoFechasNacimiento = (fechaCorte, edadMin, edadMax) => {
  const corte = fechaCorte ? new Date(`${fechaCorte}T00:00:00`) : null;
  if (!corte || isNaN(corte)) return '—';
  const y = corte.getFullYear();
  const minDate = new Date(y - Number(edadMax || 0), 0, 1);
  const maxDate = new Date(y - Number(edadMin || 0), 11, 31);
  const fmt = (d) => d.toLocaleDateString('es-ES', { day:'2-digit', month:'2-digit', year:'numeric' });
  return `${fmt(minDate)} – ${fmt(maxDate)}`;
};

function CategoryDetailModal({
  open,
  onClose,
  competenciaId,
  categoria,                 // objeto completo
  onUpdateCategoria,         // async (competenciaId, id, payload)
  setSnackbar,               // opcional
}) {
  const [form, setForm] = React.useState({
    codigo: categoria?.codigo || '',
    nombre: categoria?.nombre || '',
    edadMin: categoria?.edadMin ?? '',
    edadMax: categoria?.edadMax ?? '',
    fechaCorte: categoria?.fechaCorte || '',
  });
  const [original, setOriginal] = React.useState(form);
  const [saving, setSaving] = React.useState(false);

  // rehidratación cuando cambia la categoría
  React.useEffect(() => {
    const next = {
      codigo: categoria?.codigo || '',
      nombre: categoria?.nombre || '',
      edadMin: categoria?.edadMin ?? '',
      edadMax: categoria?.edadMax ?? '',
      fechaCorte: categoria?.fechaCorte || '',
    };
    setForm(next);
    setOriginal(next);
  }, [categoria]);

  const hasChanges = React.useMemo(() => {
    const n = (v) => (v === '' || v === null || v === undefined) ? '' : String(v).trim();
    return (
      n(form.codigo)   !== n(original.codigo) ||
      n(form.nombre)   !== n(original.nombre) ||
      Number(form.edadMin) !== Number(original.edadMin) ||
      Number(form.edadMax) !== Number(original.edadMax) ||
      form.fechaCorte  !== original.fechaCorte
    );
  }, [form, original]);

  const rangoNac = React.useMemo(
    () => calcularRangoFechasNacimiento(form.fechaCorte, form.edadMin, form.edadMax),
    [form.fechaCorte, form.edadMin, form.edadMax]
  );

  const updateField = (e) => {
    const { name, value } = e.target;
    if ((name === 'edadMin' || name === 'edadMax') && Number(value) < 0) return;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const validate = () => {
    const errors = [];
    const edadMinNum = Number(form.edadMin);
    const edadMaxNum = Number(form.edadMax);
    if (!String(form.codigo || '').trim()) errors.push('Código es requerido.');
    if (!String(form.nombre || '').trim()) errors.push('Nombre es requerido.');
    if (form.edadMin === '' || isNaN(edadMinNum)) errors.push('Edad Mín. inválida.');
    if (form.edadMax === '' || isNaN(edadMaxNum)) errors.push('Edad Máx. inválida.');
    if (!isNaN(edadMinNum) && !isNaN(edadMaxNum) && edadMinNum > edadMaxNum)
      errors.push('Edad Mín. no puede ser mayor a Edad Máx.');
    if (!form.fechaCorte) errors.push('Fecha de Corte es requerida.');
    else if (isNaN(new Date(form.fechaCorte + 'T00:00:00').getTime()))
      errors.push('Formato de Fecha de Corte inválido.');
    return { ok: errors.length === 0, errors, payload: {
      codigo: String(form.codigo || '').trim(),
      nombre: String(form.nombre || '').trim(),
      edadMin: edadMinNum,
      edadMax: edadMaxNum,
      fechaCorte: form.fechaCorte,
    }};
  };

  const handleSave = async () => {
    const { ok, errors, payload } = validate();
    if (!ok) {
      setSnackbar?.({ open: true, message: `Errores: ${errors.join(' ')}`, severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await onUpdateCategoria(competenciaId, categoria.id, payload);
      if (res?.wasUpdated) {
        setSnackbar?.({ open: true, message: 'Categoría actualizada.', severity: 'success' });
        setOriginal(form);
        onClose?.();
      } else {
        setSnackbar?.({ open: true, message: `No se pudo actualizar: ${res?.reason || 'Intente de nuevo'}`, severity: 'warning' });
      }
    } catch (e) {
      setSnackbar?.({ open: true, message: `Error: ${e.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseGuard = () => {
    if (!hasChanges) return onClose?.();
    // Confirmación simple nativa para no complicar:
    const ok = window.confirm('Hay cambios sin guardar. ¿Salir sin guardar?');
    if (ok) onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseGuard}
      fullWidth={false} 
      maxWidth="xs" 
      fullScreen={window.matchMedia('(max-width: 400px)').matches}
        PaperProps={{
    sx: {
      borderRadius: 3,    // bordes redondeados
      p: 2,               // padding interno
      maxWidth: 380,      // ancho máximo fijo
      width: '100%',      // ajusta a pantalla pequeña
      mx: 'auto',         // centra horizontalmente
    },
  }}
    >
      <DialogTitle>
        Detalle de categoría
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Header con #Cat */}
          <Box sx={(t)=>({
            display:'flex', alignItems:'center', justifyContent:'space-between',
            p: 1, borderRadius: 2,
            bgcolor: alpha(t.palette.background.paper, 0.6),
            border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`
          })}>
            <Typography sx={{ fontWeight: 700 }}>{categoria?.nombre || '—'}</Typography>
            <Chip
              size="small"
              label={`#${categoria?.catNumber ?? '—'}`}
              sx={(t)=>({
                fontWeight: 700,
                bgcolor: alpha(t.palette.primary.main, 0.08),
                color: t.palette.primary.main,
                border: `1px solid ${alpha(t.palette.primary.main, 0.2)}`
              })}
            />
          </Box>

          <TextField
            label="Código"
            name="codigo"
            value={form.codigo}
            onChange={updateField}
            fullWidth
            size="small"
          />
          <TextField
            label="Nombre"
            name="nombre"
            value={form.nombre}
            onChange={updateField}
            fullWidth
            size="small"
          />

          <Box sx={{ display:'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField
              label="Edad mínima"
              name="edadMin"
              type="number"
              value={form.edadMin}
              onChange={updateField}
              size="small"
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Edad máxima"
              name="edadMax"
              type="number"
              value={form.edadMax}
              onChange={updateField}
              size="small"
              inputProps={{ min: 0 }}
            />
          </Box>

          <TextField
            label="Fecha de corte"
            name="fechaCorte"
            type="date"
            value={form.fechaCorte || ''}
            onChange={updateField}
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <Box sx={(t)=>({
            mt: .5,
            p: 1,
            borderRadius: 2,
            bgcolor: alpha(t.palette.success.main, 0.06),
            border: `1px dashed ${alpha(t.palette.success.main, 0.35)}`
          })}>
            <Typography variant="caption" sx={{ color:'text.secondary' }}>
              Rango de nacimientos: <b>{rangoNac}</b>
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseGuard} color="secondary">Cancelar</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || saving}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CategoryDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  competenciaId: PropTypes.string.isRequired,
  categoria: PropTypes.object,
  onUpdateCategoria: PropTypes.func.isRequired,
  setSnackbar: PropTypes.func,
};
export default CategoryDetailModal;
